import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
} from "react-bootstrap";
import {
  FaMapMarkerAlt,
  FaPhoneAlt ,
  FaEnvelope,
  FaClock,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaInstagram,
  FaYoutube,
  FaPaperPlane,
} from "react-icons/fa";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import "../../styles/contact.css";
import ContactMessageService from "../../services/contactMessageService";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Scroll animation effect
  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll(".contact-page-animate-on-scroll");
      elements.forEach((element) => {
        const elementTop = element.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        if (elementTop < windowHeight - 100) {
          element.classList.add("contact-page-animated");
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Trigger on mount

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await ContactMessageService.submitContactForm(formData);

      if (response.success) {
        setSuccess(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
        setTimeout(() => setSuccess(false), 5000);
      } else {
        alert(response.message || "Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Submit error:", error);
      alert("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt />,
      title: "Visit Us",
      details: [
        "B-492, Cross Road-25",
        "Junwani Main Road, Smriti Nagar",
        "Bhilai (C.G.) - 490020",
      ],
    },
    {
      icon: <FaPhoneAlt  />,
      title: "Call Us",
      details: ["+91 77228-99444", "+91 93013-51989"],
    },
    {
      icon: <FaEnvelope />,
      title: "Email Us",
      details: ["piyush@sensible-computers.com", "contact@sensibleacademy.com"],
    },
    {
      icon: <FaClock />,
      title: "Working Hours",
      details: [
        "Monday - Saturday: 9:00 AM - 8:00 PM",
        "Sunday: 10:00 AM - 2:00 PM",
      ],
    },
  ];

  const socialLinks = [
    { icon: <FaFacebookF />, url: "https://www.facebook.com/people/Sensible-Academy-New/100083380580703/", color: "#3b5998" },
    { icon: <FaLinkedinIn />, url: "https://www.linkedin.com/company/sensible-academy/", color: "#0077b5" },
    { icon: <FaInstagram />, url: "https://www.instagram.com/sensibleacademy2005/", color: "#e4405f" },
    { icon: <FaYoutube />, url: "https://www.youtube.com/@sensibleacademy", color: "#ff0000" },
  ];

  return (
    <div className="contact-page-wrapper">
      <PublicNavbar />

      {/* Hero Section - Fixed Positioning */}
      <div className="contact-page-hero">
        <div className="contact-page-hero-bg-animation">
          <div className="contact-page-circle contact-page-circle-1"></div>
          <div className="contact-page-circle contact-page-circle-2"></div>
          <div className="contact-page-circle contact-page-circle-3"></div>
        </div>
        <Container className="contact-page-hero-container">
          <Row className="align-items-center contact-page-hero-row">
            <Col lg={8} className="mx-auto text-center">
              <div className="contact-page-hero-content-wrapper">
                <h1 className="display-3 fw-bold text-white mb-4 contact-page-hero-title contact-page-animate-on-scroll">
                  Get In <span className="text-warning">Touch</span>
                </h1>
                <p className="lead text-white mb-4 contact-page-hero-description contact-page-animate-on-scroll">
                  Have questions? We'd love to hear from you. Send us a message
                  and we'll respond as soon as possible.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
        <div className="contact-page-hero-wave">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 270"
            preserveAspectRatio="none"
          >
            <path
              fill="#ffffff"
              fillOpacity="1"
              d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,170.7C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>
      </div>

      {/* Contact Info Cards */}
      <Container className="contact-page-container py-5">
        <Row className="g-4">
          {contactInfo.map((info, idx) => (
            <Col lg={3} md={6} key={idx}>
              <Card className="contact-page-info-card h-100 border-0 shadow-sm contact-page-animate-on-scroll">
                <Card.Body className="text-center">
                  <div className="contact-page-info-icon-wrapper mb-3">{info.icon}</div>
                  <h5 className="fw-bold mb-3">{info.title}</h5>
                  {info.details.map((detail, i) => (
                    <p key={i} className="text-muted mb-1">
                      {detail}
                    </p>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      {/* Map and Form Section */}
      <Container className="contact-page-container py-5">
        <Row className="g-4">
          {/* Google Map */}
          <Col lg={6}>
            <Card className="contact-page-map-card border-0 shadow-sm h-100 contact-page-animate-on-scroll">
              <Card.Body className="p-0">
                <div className="contact-page-map-container">
                  <iframe
                    title="Sensible Academy Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13095.718439692417!2d81.30993013985774!3d21.21225097789625!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a293dfbcc32b0d3%3A0x402612b4ce34adf5!2sSensible%20Academy%20-%20Vocational%20Training%20Center%20in%20Bhilai-Durg!5e1!3m2!1sen!2sin!4v1775306348523!5m2!1sen!2sin"
                    allowFullScreen=""
                    loading="lazy"
                    className="contact-page-map-iframe"
                  ></iframe>
                </div>
                <div className="contact-page-map-overlay p-4">
                  <h5 className="fw-bold mb-2">Visit Our Campus</h5>
                  <p className="text-muted mb-0">
                    <FaMapMarkerAlt className="text-primary me-2" />
                    B-492, Cross Road-25, Junwani Main Road, Smriti Nagar,
                    Bhilai (C.G.)
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Contact Form */}
          <Col lg={6}>
            <Card className="contact-page-form-card border-0 shadow-sm contact-page-animate-on-scroll">
              <Card.Body className="p-4">
                <h3 className="fw-bold mb-4">Send Us a Message</h3>

                {success && (
                  <Alert variant="success" className="mb-4">
                    Thank you for your message! We'll get back to you soon.
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Your Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your full name"
                          required
                          className="contact-page-form-control-lg"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Email Address
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter your email"
                          required
                          className="contact-page-form-control-lg"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold">
                          Phone Number
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Enter your phone number"
                          className="contact-page-form-control-lg"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label className="fw-semibold">Subject</Form.Label>
                        <Form.Control
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="Enter subject"
                          required
                          className="contact-page-form-control-lg"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      Your Message
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Type your message here..."
                      rows={5}
                      required
                      className="contact-page-form-control-lg"
                    />
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-100 contact-page-submit-btn"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane className="me-2" /> Send Message
                      </>
                    )}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Social Media Section */}
      <div className="contact-page-social-section py-5">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <div className="contact-page-social-content contact-page-animate-on-scroll">
                <h2 className="display-5 fw-bold text-white mb-4">
                  Connect With Us
                </h2>
                <p className="lead text-white mb-5">
                  Follow us on social media for updates, news, and educational
                  content
                </p>
                <div className="contact-page-social-links">
                  {socialLinks.map((social, idx) => (
                    <a
                      key={idx}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-page-social-link"
                      style={{ "--contact-page-social-color": social.color }}
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* FAQ Section */}
      <Container className="contact-page-container py-5">
        <h2 className="text-center display-5 fw-bold mb-5">
          <span className="text-primary">Frequently Asked</span> Questions
        </h2>
        <Row className="g-4">
          <Col md={6}>
            <Card className="contact-page-faq-card h-100 border-0 shadow-sm contact-page-animate-on-scroll">
              <Card.Body>
                <h5 className="fw-bold mb-3">What courses do you offer?</h5>
                <p className="text-muted">
                  We offer a wide range of IT courses including Full Stack Web
                  Development, Python, Java, Android Development, Data
                  Structures, and more. Check our Courses page for complete
                  list.
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="contact-page-faq-card h-100 border-0 shadow-sm contact-page-animate-on-scroll">
              <Card.Body>
                <h5 className="fw-bold mb-3">
                  Do you provide placement assistance?
                </h5>
                <p className="text-muted">
                  Yes, we provide 100% placement assistance including mock
                  interviews, resume building, and direct connections with our
                  hiring partners.
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="contact-page-faq-card h-100 border-0 shadow-sm contact-page-animate-on-scroll">
              <Card.Body>
                <h5 className="fw-bold mb-3">What are your class timings?</h5>
                <p className="text-muted">
                  We offer flexible batch timings including morning, evening,
                  and weekend batches to accommodate working professionals and
                  students.
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="contact-page-faq-card h-100 border-0 shadow-sm contact-page-animate-on-scroll">
              <Card.Body>
                <h5 className="fw-bold mb-3">Do you offer online classes?</h5>
                <p className="text-muted">
                  Yes, we offer both classroom and online training options. You
                  can choose what works best for your schedule and learning
                  preference.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* CTA Section */}
      <div className="contact-page-cta-section py-5">
        <Container className="text-center">
          <div className="contact-page-cta-content contact-page-animate-on-scroll">
            <h2 className="display-5 fw-bold text-white mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="lead text-white mb-4">
              Join thousands of successful students who transformed their
              careers with us
            </p>
            <Button
              variant="light"
              size="lg"
              className="me-3 contact-page-cta-btn"
              onClick={() => (window.location.href = "/register")}
            >
              Enroll Now
            </Button>
            <Button
              variant="outline-light"
              size="lg"
              className="contact-page-cta-outline-btn"
              onClick={() => (window.location.href = "/courses")}
            >
              Browse Courses
            </Button>
          </div>
        </Container>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;